import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/feature/SEOHead';
import { blogPosts } from '../../../mocks/blogs';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = slug ? blogPosts.find((p) => p.slug === slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-navy-primary mb-4">
            Article Not Found
          </h1>
          <Link to="/blog" className="text-gold-accent hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.heroImage,
    url: `${SITE_URL}/blog/${post.slug}`,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'True Passage Ventures',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'True Passage Ventures',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/vite.svg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${post.title} | True Passage Ventures Blog`}
        description={post.excerpt}
        keywords={`${post.category}, India sourcing, supply chain, global trade, True Passage Ventures`}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.heroImage}
        schema={articleSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-navy-primary via-navy-primary to-navy-primary/90">
        <div className="max-w-[900px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </button>

            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-gold-accent text-white text-sm font-medium rounded-full whitespace-nowrap">
                {post.category}
              </span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                {post.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative w-full h-[400px] lg:h-[500px]">
        <img
          src={post.heroImage}
          alt={post.title}
          title={`${post.title} – True Passage Ventures`}
          className="w-full h-full object-cover object-top"
        />
      </section>

      {/* Article Content */}
      <article className="py-16">
        <div className="max-w-[800px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-lg max-w-none"
            style={{ color: '#0B1F33' }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: post.content }}
              className="blog-content"
            />
          </motion.div>

          {/* CTA Box */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-16 p-8 bg-gradient-to-br from-navy-primary to-navy-primary/90 rounded-lg text-center"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Source from India?
            </h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Let True Passage Ventures handle your sourcing needs. From supplier
              discovery to quality control and logistics, we make global trade
              simple.
            </p>
            <Link
              to="/request-quote"
              className="inline-block px-8 py-4 bg-gold-accent text-white font-medium rounded-lg hover:bg-gold-accent/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </Link>
          </motion.div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-3xl font-bold text-navy-primary mb-8">
            More Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts
              .filter((p) => p.slug !== slug)
              .slice(0, 3)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="group block"
                >
                  <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={relatedPost.heroImage}
                        alt={relatedPost.title}
                        title={`${relatedPost.title} – True Passage Ventures`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="inline-block px-3 py-1 bg-gold-accent/10 text-gold-accent text-xs font-medium rounded-full mb-3 whitespace-nowrap">
                        {relatedPost.category}
                      </span>
                      <h3 className="text-lg font-bold text-navy-primary mb-2 group-hover:text-gold-accent transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-navy-primary/60">
                        {relatedPost.readTime}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
